import { useEffect, useMemo, useState } from 'react';
import { ApiError, errorMessage, patch } from '../../api/client';
import type { BikeModel, DocKind, ModelDocuments } from '../../api/types';
import { DocumentSlot } from '../../components/media';
import { useFeedback } from '../../components/ui';
import { useDraft, useUnloadGuard } from '../../hooks';
import { tk, useT } from '../../i18n';
import { SaveBar } from './SaveBar';
import type { TabProps } from './shared';

/** Los tres documentos de la página de Servicios, en el orden del sitio, con su campo en la API (las etiquetas se traducen con t()). */
const DOCS: { kind: DocKind; label: string; field: string }[] = [
  { kind: 'owner', label: tk('Manual del propietario'), field: 'ownerManualMediaId' },
  { kind: 'maintenance', label: tk('Plan de mantenimiento'), field: 'maintenanceMediaId' },
  { kind: 'service', label: tk('Manual de servicio'), field: 'serviceManualMediaId' },
];

export function DocumentsTab({ model, onSaved, onDirty }: TabProps) {
  const { t, tr } = useT();
  const initial = useMemo<ModelDocuments>(() => ({ ...model.documents }), [model]);
  const { draft, setDraft, dirty, reset, markSaved } = useDraft<ModelDocuments>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { toast } = useFeedback();
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const save = async () => {
    const submitted = draft;
    setBusy(true);
    setError(null);
    try {
      const body = Object.fromEntries(DOCS.map((d) => [d.field, draft[d.kind]?.id ?? null]));
      const { model: saved } = await patch<{ model: BikeModel }>(`/admin/models/${model.id}`, body);
      onSaved(saved);
      markSaved(submitted);
      toast(t('Documentos guardados'));
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  const count = DOCS.filter((d) => draft[d.kind]).length;

  return (
    <div className="stack">
      <section className="panel panel-pad stack">
        <div>
          <h2 className="section-title">{t('Documentos para descargar')}</h2>
          <p className="sub" style={{ margin: 0 }}>
            {tr('Salen en la página {page} del sitio, en la fila de la {name}. Solo PDF. Los que falten se ven como «Próximamente». Los cambios llegan al sitio al publicar.', {
              page: <span className="mono">/servicios/</span>,
              name: model.name,
            })}
          </p>
        </div>
        <div className="stack" style={{ gap: 10 }}>
          {DOCS.map((d) => (
            <DocumentSlot
              key={d.kind}
              label={t(d.label)}
              emptyHint={t('Sin PDF: en el sitio dice «Próximamente»')}
              media={draft[d.kind]}
              onChange={(m) => setDraft((prev) => ({ ...prev, [d.kind]: m }))}
            />
          ))}
        </div>
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>
          {count === 3 ? t('Están los tres documentos.') : t('{n} de 3 documentos.', { n: count })} {t('Puedes arrastrar el PDF sobre su fila.')}
        </p>
      </section>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
